import type { GeometryElement } from 'jsxgraph';
import type { BoundLabel } from '../label/label.bind';
import type { LineStyle, PolygonStyle, PointStyle } from '../theme/theme.model';
import type { PointInput } from '../line/line.model';

/**
 * Vertex input for polygon - same as line point input.
 */
export type PolygonVertex = PointInput;

/**
 * Polygon style with vertex and border style overrides.
 *
 * Style cascade:
 * - style.vertex: Default style for all vertices (any PointStyle property)
 * - style.vertexPerIndex[i]: Per-vertex style overrides
 * - style.border: Default style for all borders (any LineStyle property)
 * - style.borderPerEdge[i]: Per-border style overrides
 *
 * Labels are configured via the `label` property at each level of the hierarchy.
 */
export interface PolygonStyleWithChildren extends PolygonStyle {
  /** Default style for all vertices */
  vertex?: PointStyle;
  /** Default style for all borders */
  border?: LineStyle;
  /** Per-vertex style overrides by index */
  vertexPerIndex?: Record<number, PointStyle>;
  /** Per-border style overrides by edge index */
  borderPerEdge?: Record<number, LineStyle>;
}

/**
 * Arguments for creating a polygon.
 * Style overrides cascade through the style property hierarchy.
 */
export interface PolygonArgs {
  /** Vertices of the polygon (coords, existing elements, or point names) */
  vertices: PolygonVertex[];
  /** Polygon name (used for labeling) */
  name?: string;
  /** Style overrides including vertex and border styles */
  style?: PolygonStyleWithChildren;
  /** Names for vertices (defaults to A, B, C, ...) */
  vertexNames?: string[];
}

/**
 * Result of polygon creation.
 */
export interface PolygonResult {
  /** The created polygon element */
  polygon: GeometryElement;
  /** Array of vertex points (created or referenced) */
  vertices: GeometryElement[];
  /** Array of border lines */
  borders: GeometryElement[];
  /** Labels for vertices (always created; visibility controlled via config) */
  vertexLabels: BoundLabel[];
  /** Labels for borders (always created; visibility controlled via config) */
  borderLabels: BoundLabel[];
  /** The label for the polygon itself (always created; visibility controlled via config) */
  polygonLabel: BoundLabel;
  /** Helper elements created for labels (midpoints, centroids) — needed for cleanup */
  helpers: GeometryElement[];
}

/**
 * Generate default vertex names: A, B, C, ..., Z, AA, AB, ...
 */
export function generateVertexNames(count: number): string[] {
  const names: string[] = [];
  for (let i = 0; i < count; i++) {
    if (i < 26) {
      names.push(String.fromCharCode(65 + i)); // A-Z
    } else {
      const first = Math.floor(i / 26) - 1;
      const second = i % 26;
      names.push(String.fromCharCode(65 + first) + String.fromCharCode(65 + second)); // AA, AB, etc.
    }
  }
  return names;
}
